<template>
  <v-app>
    <!-- Loading Overlay -->
    <div v-if="isGlobalLoading" class="global-loading-overlay">
      <v-progress-circular
        indeterminate
        color="#FF6B00" 
        size="64"
      ></v-progress-circular>
    </div>

    <Header v-if="!$route.meta.hideHeader && !isAdminPanel && (isAuthenticated || $route.name === 'RFIDScan')" />
    <router-view />
    <notifications group="global" position="top right" />
    <ConfirmModal ref="timeoutConfirm" />
  </v-app>
</template>

<script>
import { computed, watch, ref, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import moment from 'moment';
import Header from '@components/Header/Header.vue';
import ConfirmModal from '@modals/ConfirmModal.vue';
import useAuthTimeout from '@composable/useAuthTimeout';
import store from "./store";

export default {
  name: 'App',
  components: {
    Header,
    ConfirmModal,
  },
  setup() {
    const store = useStore();
    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const timeoutConfirm = ref(null);
    const instance = getCurrentInstance();
    const { proxy } = instance;

    const { showWarningModal, extendSession, forceLogout } = useAuthTimeout(timeoutConfirm);

    const isAuthenticated = computed(() => store.getters["auth/getIsAuthenticated"]);
    const isRfidLogin = computed(() => store.getters["auth/getIsRfidLogin"]);
    const isGlobalLoading = computed(() => store.getters["auth/getIsGlobalLoading"]);

    watch(showWarningModal, (newValue) => {
      if (newValue && timeoutConfirm.value) {
        const warningDurationSeconds = 15;

        timeoutConfirm.value.open(
          t('message.sessionExpireWarningTitle'),
          t('message.sessionExpireWarningText'),
          {
            color: 'orange',
            confirmText: t('message.continueSession'),
            cancelText: t('message.logout'),
            initialCountdown: warningDurationSeconds
          }
        )
        .then((confirmed) => {
            if (confirmed) {
                extendSession(false);
            } else {
                forceLogout();
            }
        });
      } else if (!newValue && timeoutConfirm.value?.isActive) {
        timeoutConfirm.value.close();
      }
    });

    const fetchOnCreate = () => {
      if (isAuthenticated.value) {
        store.dispatch('auth/auth');
        store.dispatch('auth/permissions');
        if (proxy.$api) {
          proxy.$api.rooms.fetchData();
          proxy.$api.teachers.fetchData();
          proxy.$api.students.fetchData();
          proxy.$api.subjects.fetchData();
          proxy.$api.categories.fetchData();
          proxy.$api.statuses.fetchData();
          proxy.$api.roles.getRoles();
        } else {
          console.error("$api is not available on the component instance.");
        }
      }
    };

    const handleCtrlQ = async (event) => {
      if (event.ctrlKey && event.key === 'q') {
        event.preventDefault();

        if (route.name === 'RFIDScan') {
          localStorage.removeItem('user-token');
          localStorage.removeItem('isRfidLogin');
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('lastLogoutWasScreenView');
          store.commit("auth/LOGOUT_SUCCESS");
          router.push({ name: 'SignIn' });
        } else if (isAuthenticated.value) {
          const revalToken = store.getters["auth/getRevalidationToken"];

          try {
            const wasRfidBeforeLogout = isRfidLogin.value;
            await store.dispatch("auth/logout");
            
            if (revalToken) {
              try {
                await store.dispatch("auth/resumeSession", { revalidation_token: revalToken });
                await store.dispatch("auth/permissions");
                router.push({ name: 'RFIDScan' });
              } catch (resumeError) {
                console.warn("[Ctrl+Q] Failed to resume session:", resumeError?.message || resumeError);
                router.push({ name: 'SignIn' });
              }
            } else {
              router.push({ name: 'SignIn' });
            }
          } catch (logoutError) {
            console.error("[Ctrl+Q Logout] Initial logout dispatch failed:", logoutError);
            router.push({ name: 'SignIn' });
          }
        }
      }
    };

    onMounted(() => {
      const selectedLanguage = store.getters["language/getSelectedLanguage"] || "lv";
      const locale = selectedLanguage === "English" ? "en" : "lv";
      moment.locale(locale);
      
      fetchOnCreate();

      document.addEventListener('keydown', handleCtrlQ);

      if (proxy.$root && timeoutConfirm.value) {
        proxy.$root.$confirm = timeoutConfirm.value;
      }
    });

    onUnmounted(() => {
      document.removeEventListener('keydown', handleCtrlQ);
    });

    watch(route, (/* newRoute, oldRoute */) => {
      if (isGlobalLoading.value) {
        store.commit('auth/SET_GLOBAL_LOADING', false);
      }
    });

    return {
      isAuthenticated,
      timeoutConfirm,
      isAdminPanel: computed(() => route.path.includes('admin')),
      isGlobalLoading,
      extendSession
    };
  },
};
</script>

<style lang="scss">
@use '@/styles/_index' as *;
@use './styles/style.scss' as *;
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');
@import url('vuetify/styles');

body {
  font-family: $defaultFont;
}

.global-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 255, 255, 0.9); // Slightly transparent white background
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999; // Ensure it's on top
}
</style>
